import { BaseService } from '@/infra/services/base-service';
import { GetBeraJobSummariesMapper, GetBeraReportV2Mapper, GetBeraVersionsMapper } from '@/core/mapper/bera';
import type {
	BeraJobSummaryListPage,
	BeraPdfDownload,
	BeraReportV2Details,
	BeraRevisionSource,
	BeraSummaryFinishV2,
	BeraSummaryResultV2,
	BeraVersion
} from '@/types/Bera';
import {
	CreateBeraRevisionRequestDTO,
	CreateBeraRevisionResponseDTO,
	DeleteBeraJobSummaryLineageRequestDTO,
	DeleteBeraJobSummaryLineageResponseDTO,
	DeleteBeraJobSummaryRequestDTO,
	DeleteBeraJobSummaryResponseDTO,
	DeleteBeraReportV2RequestDTO,
	DeleteBeraReportV2ResponseDTO,
	DownloadPdfBeraJobSummaryV2RequestDTO,
	DownloadPdfBeraJobSummaryV2ResponseDTO,
	DownloadPdfBeraReportV2RequestDTO,
	DownloadPdfBeraReportV2ResponseDTO,
	FinishSummaryV2RequestDTO,
	FinishSummaryV2ResponseDTO,
	GetBeraJobSummariesRequestDTO,
	GetBeraJobSummariesResponseDTO,
	GetBeraReportV2RequestDTO,
	GetBeraReportV2ResponseDTO,
	GetBeraRevisionSourceRequestDTO,
	GetBeraRevisionSourceResponseDTO,
	GetBeraVersionsRequestDTO,
	GetBeraVersionsResponseDTO,
	GetSummaryResultV2RequestDTO,
	GetSummaryResultV2ResponseDTO,
	PatchBeraJobSummaryRequestDTO,
	PatchBeraJobSummaryResponseDTO,
	RetryPatchBeraJobSummaryRequestDTO,
	RetryPatchBeraJobSummaryResponseDTO,
	RetryReportClipRequestDTO,
	RetryReportClipResponseDTO,
	UpdateBeraReportV2RequestDTO,
	UpdateBeraReportV2ResponseDTO
} from '@/core/dto/bera';

interface ScopeParams {
	organization_id: string;
	company_id: string;
}

export class BeraV2Service extends BaseService<GetBeraReportV2ResponseDTO> {
	private static instance: BeraV2Service;

	private constructor(public readonly basePath: string = '/bera') {
		super();
	}

	public static getInstance(): BeraV2Service {
		if (!BeraV2Service.instance) {
			BeraV2Service.instance = new BeraV2Service();
		}
		return BeraV2Service.instance;
	}

	public async showReport(params: GetBeraReportV2RequestDTO): Promise<BeraReportV2Details> {
		const url = this.toReportPath(params.id);
		const query = BeraV2Service.toScopeQuery(params);
		const { data } = await this.getInstance().get<GetBeraReportV2ResponseDTO>(url, { params: query });
		return new GetBeraReportV2Mapper(data).toDomain();
	}

	public async updateReport(params: UpdateBeraReportV2RequestDTO): Promise<UpdateBeraReportV2ResponseDTO> {
		const url = this.toReportPath(params.id);
		const body = {
			organization_id: params.organization_id,
			company_id: params.company_id,
			task_id: params.task_id,
			task_time: params.task_time,
			task_time_format: params.task_time_format,
			has_known_injury: params.has_known_injury,
			collection_date: params.collection_date,
			has_no_risk: params.has_no_risk,
			step: params.step
		};
		const { data } = await this.getInstance().put<UpdateBeraReportV2ResponseDTO>(url, body);
		return data;
	}

	public async removeReport(params: DeleteBeraReportV2RequestDTO): Promise<DeleteBeraReportV2ResponseDTO> {
		const url = this.toReportPath(params.id);
		const query = BeraV2Service.toScopeQuery(params);
		const { data } = await this.getInstance().delete<DeleteBeraReportV2ResponseDTO>(url, { params: query });
		return data;
	}

	public async downloadReportPdf(params: DownloadPdfBeraReportV2RequestDTO): Promise<BeraPdfDownload> {
		const url = `${this.toReportPath(params.id)}/pdf`;
		const body = { ...BeraV2Service.toScopeQuery(params), locale: params.locale };
		const { data } = await this.getInstance().post<DownloadPdfBeraReportV2ResponseDTO>(url, body);
		return data;
	}

	public async retryReportClip(params: RetryReportClipRequestDTO): Promise<RetryReportClipResponseDTO> {
		const url = `${this.toReportPath(params.id)}/clip/retry`;
		const body = BeraV2Service.toScopeQuery(params);
		const { data } = await this.getInstance().post<RetryReportClipResponseDTO>(url, body);
		return data;
	}

	public async showJobSummaryResult(params: GetSummaryResultV2RequestDTO): Promise<BeraSummaryResultV2> {
		const url = `${this.basePath}/v2/job-summary/${params.id}/result`;
		const query = BeraV2Service.toScopeQuery(params);
		const { data } = await this.getInstance().get<GetSummaryResultV2ResponseDTO>(url, { params: query });
		return data;
	}

	public async finishJobSummary(params: FinishSummaryV2RequestDTO): Promise<BeraSummaryFinishV2> {
		const url = `${this.basePath}/v2/job-summary/${params.id}/finish`;
		const body = BeraV2Service.toScopeQuery(params);
		const { data } = await this.getInstance().post<FinishSummaryV2ResponseDTO>(url, body);
		return data;
	}

	public async downloadJobSummaryPdf(params: DownloadPdfBeraJobSummaryV2RequestDTO): Promise<BeraPdfDownload> {
		const url = `${this.basePath}/v2/job-summary/${params.id}/pdf`;
		const body = { ...BeraV2Service.toScopeQuery(params), locale: params.locale };
		const { data } = await this.getInstance().post<DownloadPdfBeraJobSummaryV2ResponseDTO>(url, body);
		return data;
	}

	public async listVersions(params: GetBeraVersionsRequestDTO): Promise<BeraVersion[]> {
		const url = `${this.basePath}/v2/job-summary/${params.id}/versions`;
		const query = BeraV2Service.toScopeQuery(params);
		const { data } = await this.getInstance().get<GetBeraVersionsResponseDTO>(url, { params: query });
		return new GetBeraVersionsMapper(data).toDomain();
	}

	public async createRevision(params: CreateBeraRevisionRequestDTO): Promise<CreateBeraRevisionResponseDTO> {
		const url = `${this.basePath}/v2/job-summary/${params.id}/revisions`;
		const body = {
			organization_id: params.organization_id,
			company_id: params.company_id,
			revision_type: params.revision_type,
			report_name: params.report_name,
			evaluator_id: params.evaluator_id,
			operator_evaluated: params.operator_evaluated,
			work_center: params.work_center
		};
		const { data } = await this.getInstance().post<CreateBeraRevisionResponseDTO>(url, body);
		return data;
	}

	public async patchJobSummary(params: PatchBeraJobSummaryRequestDTO): Promise<PatchBeraJobSummaryResponseDTO> {
		const url = `${this.basePath}/v2/job-summary/${params.id}/patch`;
		const body = {
			organization_id: params.organization_id,
			company_id: params.company_id,
			assignments: params.assignments
		};
		const { data } = await this.getInstance().put<PatchBeraJobSummaryResponseDTO>(url, body);
		return data;
	}

	public async retryPatch(params: RetryPatchBeraJobSummaryRequestDTO): Promise<RetryPatchBeraJobSummaryResponseDTO> {
		const url = `${this.basePath}/v2/job-summary/${params.id}/patch/retry`;
		const body = BeraV2Service.toScopeQuery(params);
		const { data } = await this.getInstance().post<RetryPatchBeraJobSummaryResponseDTO>(url, body);
		return data;
	}

	public async findRevisionSource(params: GetBeraRevisionSourceRequestDTO): Promise<BeraRevisionSource | null> {
		const url = `${this.basePath}/v2/job-summary/revision-source`;
		const scope = BeraV2Service.toScopeQuery(params);
		const query = { ...scope, cycle_id: params.cycle_id, workstation_id: params.workstation_id };
		const { data } = await this.getInstance().get<GetBeraRevisionSourceResponseDTO>(url, { params: query });
		const source = data.source;
		return source;
	}

	public async listJobSummaries(params: GetBeraJobSummariesRequestDTO): Promise<BeraJobSummaryListPage> {
		const url = `${this.basePath}/job-summary/list`;
		const scope = BeraV2Service.toScopeQuery(params);
		const query = { ...scope, ...params.filter };
		const { data } = await this.getInstance().get<GetBeraJobSummariesResponseDTO>(url, { params: query });
		return new GetBeraJobSummariesMapper(data).toDomain();
	}

	public async removeJobSummary(params: DeleteBeraJobSummaryRequestDTO): Promise<DeleteBeraJobSummaryResponseDTO> {
		const url = `${this.basePath}/job-summary/${params.id}`;
		const query = BeraV2Service.toScopeQuery(params);
		const { data } = await this.getInstance().delete<DeleteBeraJobSummaryResponseDTO>(url, { params: query });
		return data;
	}

	public async removeJobSummaryLineage(
		params: DeleteBeraJobSummaryLineageRequestDTO
	): Promise<DeleteBeraJobSummaryLineageResponseDTO> {
		const url = `${this.basePath}/v2/job-summary/${params.id}`;
		const query = BeraV2Service.toScopeQuery(params);
		const { data } = await this.getInstance().delete<DeleteBeraJobSummaryLineageResponseDTO>(url, {
			params: query
		});
		return data;
	}

	private toReportPath(id: string): string {
		const url = `${this.basePath}/v2/report/${id}`;
		return url;
	}

	private static toScopeQuery(params: ScopeParams): ScopeParams {
		const query = { organization_id: params.organization_id, company_id: params.company_id };
		return query;
	}
}
